const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

class StockService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/stock`;
  }

  // Auth header'ı oluştur
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': localStorage.getItem('companyId')
    };
  }

  // Genel API çağrısı
  async apiCall(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: this.getAuthHeaders(),
        ...options
      };

      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }

  // Ürün Yönetimi API'leri
  async getProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/products?${queryString}`);
  }

  async getProductById(id) {
    return this.apiCall(`/products/${id}`);
  }

  async createProduct(productData) {
    return this.apiCall('/products', {
      method: 'POST',
      body: JSON.stringify(productData)
    });
  }

  async updateProduct(id, productData) {
    return this.apiCall(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
  }

  async deleteProduct(id) {
    return this.apiCall(`/products/${id}`, {
      method: 'DELETE'
    });
  }

  // Gelişmiş Arama API'leri
  async searchByOem(oemCode) {
    return this.apiCall(`/search/oem?code=${encodeURIComponent(oemCode)}`);
  }

  async searchByReference(referenceCode) {
    return this.apiCall(`/search/reference?code=${encodeURIComponent(referenceCode)}`);
  }

  async searchByCrossReference(crossReferenceCode) {
    return this.apiCall(`/search/cross-reference?code=${encodeURIComponent(crossReferenceCode)}`);
  }

  async advancedSearch(searchParams) {
    const queryString = new URLSearchParams(searchParams).toString();
    return this.apiCall(`/search/advanced?${queryString}`);
  }

  // Toplama Listesi API'leri
  async getPickLists(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/pick-lists?${queryString}`);
  }

  async getPickListById(id) {
    return this.apiCall(`/pick-lists/${id}`);
  }

  async createPickList(pickListData) {
    return this.apiCall('/pick-lists', {
      method: 'POST',
      body: JSON.stringify(pickListData)
    });
  }

  async updatePickList(id, pickListData) {
    return this.apiCall(`/pick-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pickListData)
    });
  }

  async deletePickList(id) {
    return this.apiCall(`/pick-lists/${id}`, {
      method: 'DELETE'
    });
  }

  async startPicking(id) {
    return this.apiCall(`/pick-lists/${id}/start`, {
      method: 'POST'
    });
  }

  async completePicking(id) {
    return this.apiCall(`/pick-lists/${id}/complete`, {
      method: 'POST'
    });
  }

  async optimizePickLists(optimizationParams) {
    return this.apiCall('/pick-lists/optimize', {
      method: 'POST',
      body: JSON.stringify(optimizationParams)
    });
  }

  // Batch Pick List API'leri
  async getBatchPickLists(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/batch-pick-lists?${queryString}`);
  }

  async createBatchPickList(batchData) {
    return this.apiCall('/batch-pick-lists', {
      method: 'POST',
      body: JSON.stringify(batchData)
    });
  }

  async updateBatchPickList(id, batchData) {
    return this.apiCall(`/batch-pick-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batchData)
    });
  }

  async deleteBatchPickList(id) {
    return this.apiCall(`/batch-pick-lists/${id}`, {
      method: 'DELETE'
    });
  }

  // Kategori ve Marka API'leri
  async getCategories() {
    return this.apiCall('/categories');
  }

  async getBrands() {
    return this.apiCall('/brands');
  }

  async getUnits() {
    return this.apiCall('/units');
  }

  async getSuppliers() {
    return this.apiCall('/suppliers');
  }

  async getWarehouses() {
    return this.apiCall('/warehouses');
  }

  // Stok Rezervasyonu API'leri
  async getStockReservations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/stock-reservations?${queryString}`);
  }

  async createStockReservation(reservationData) {
    return this.apiCall('/stock-reservations', {
      method: 'POST',
      body: JSON.stringify(reservationData)
    });
  }

  async updateStockReservation(id, reservationData) {
    return this.apiCall(`/stock-reservations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reservationData)
    });
  }

  async deleteStockReservation(id) {
    return this.apiCall(`/stock-reservations/${id}`, {
      method: 'DELETE'
    });
  }

  // Depo Konumu API'leri
  async getWarehouseLocations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/warehouse-locations?${queryString}`);
  }

  async createWarehouseLocation(locationData) {
    return this.apiCall('/warehouse-locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
  }

  async updateWarehouseLocation(id, locationData) {
    return this.apiCall(`/warehouse-locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData)
    });
  }

  async deleteWarehouseLocation(id) {
    return this.apiCall(`/warehouse-locations/${id}`, {
      method: 'DELETE'
    });
  }

  // B2B/B2C Ayarları API'leri
  async getB2BProductSettings(productId) {
    return this.apiCall(`/products/${productId}/b2b-settings`);
  }

  async updateB2BProductSettings(productId, settings) {
    return this.apiCall(`/products/${productId}/b2b-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  async getB2CProductSettings(productId) {
    return this.apiCall(`/products/${productId}/b2c-settings`);
  }

  async updateB2CProductSettings(productId, settings) {
    return this.apiCall(`/products/${productId}/b2c-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // İstatistik API'leri
  async getProductStatistics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/statistics/products?${queryString}`);
  }

  async getPickListStatistics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/statistics/pick-lists?${queryString}`);
  }

  async getWarehouseStatistics(warehouseId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiCall(`/statistics/warehouses/${warehouseId}?${queryString}`);
  }

  // Rapor API'leri
  async exportProducts(format = 'excel', filters = {}) {
    const queryString = new URLSearchParams({ format, ...filters }).toString();
    return this.apiCall(`/export/products?${queryString}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeaders(),
        'Accept': format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv'
      }
    });
  }

  async exportPickLists(format = 'excel', filters = {}) {
    const queryString = new URLSearchParams({ format, ...filters }).toString();
    return this.apiCall(`/export/pick-lists?${queryString}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeaders(),
        'Accept': format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv'
      }
    });
  }

  // Toplu İşlem API'leri
  async bulkUpdateProducts(updates) {
    return this.apiCall('/products/bulk-update', {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async bulkDeleteProducts(productIds) {
    return this.apiCall('/products/bulk-delete', {
      method: 'DELETE',
      body: JSON.stringify({ productIds })
    });
  }

  async importProducts(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Import seçeneklerini ekle
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return this.apiCall('/products/import', {
      method: 'POST',
      headers: {
        'Authorization': this.getAuthHeaders().Authorization,
        'Company-Id': this.getAuthHeaders()['Company-Id']
      },
      body: formData
    });
  }

  // WebSocket bağlantısı için (gerçek zamanlı güncellemeler)
  connectWebSocket(onMessage) {
    const token = localStorage.getItem('authToken');
    const companyId = localStorage.getItem('companyId');
    
    const ws = new WebSocket(`${API_BASE_URL.replace('http', 'ws')}/ws/stock?token=${token}&companyId=${companyId}`);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('WebSocket message parse error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };

    return ws;
  }

  // Cache yönetimi
  clearCache() {
    // LocalStorage cache'ini temizle
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('stock_cache_')) {
        localStorage.removeItem(key);
      }
    });
  }

  // Cache'li API çağrısı
  async cachedApiCall(endpoint, cacheKey, ttl = 300000) { // 5 dakika default TTL
    const cacheKeyFull = `stock_cache_${cacheKey}`;
    const cached = localStorage.getItem(cacheKeyFull);
    
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < ttl) {
        return data;
      }
    }

    const data = await this.apiCall(endpoint);
    
    localStorage.setItem(cacheKeyFull, JSON.stringify({
      data,
      timestamp: Date.now()
    }));

    return data;
  }
}

// Singleton instance
const stockService = new StockService();

export default stockService;
